import {
  Divider,
  Group,
  NumberInput,
  Paper,
  Stack,
  Switch,
  Text,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { WebBotProviderData } from '@/API';

interface WebBotResponsiveTabProps {
  form: UseFormReturnType<WebBotProviderData>;
}

export function WebBotResponsiveTab({ form }: WebBotResponsiveTabProps) {
  const resp = form.values.responsive;

  return (
    <Stack gap="md">
      <Paper withBorder p="sm" radius="md">
        <Stack gap="sm">
          <Text size="sm" fw={500}>
            基本響應式設定
          </Text>
          <Divider style={{ opacity: 0.6 }} />
          <NumberInput
            label="響應式判定斷點 (px)"
            description="當螢幕寬度小於等於此數值時，將啟動響應式設定與全螢幕規則"
            placeholder="預設 768"
            min={300}
            max={2000}
            {...form.getInputProps('responsive.breakpoint')}
            value={resp?.breakpoint ?? ''}
          />
          <Switch
            label="響應式強制全螢幕"
            description="進入響應式尺寸時，自動強制全螢幕且隱藏視窗縮放切換按鈕"
            checked={resp?.forceFullScreen ?? false}
            {...form.getInputProps('responsive.forceFullScreen', { type: 'checkbox' })}
          />
        </Stack>
      </Paper>

      <Paper withBorder p="sm" radius="md">
        <Stack gap="sm">
          <Text size="sm" fw={500}>
            響應式視窗相關設定
          </Text>
          <Divider style={{ opacity: 0.6 }} />
          <Group grow>
            <NumberInput
              label="響應式視窗寬度 (px)"
              placeholder="未設定則與桌面版相同"
              {...form.getInputProps('responsive.windowWidth')}
              value={resp?.windowWidth ?? ''}
            />
            <NumberInput
              label="響應式視窗高度 (px)"
              placeholder="未設定則與桌面版相同"
              {...form.getInputProps('responsive.windowHeight')}
              value={resp?.windowHeight ?? ''}
            />
          </Group>
          <Group grow align="flex-start">
            <NumberInput
              label="響應式視窗水平微調 X (px)"
              placeholder="未設定則與桌面版相同"
              {...form.getInputProps('responsive.windowOffsetX')}
              value={resp?.windowOffsetX ?? ''}
            />
            <NumberInput
              label="響應式視窗垂直微調 Y (px)"
              placeholder="未設定則與桌面版相同"
              {...form.getInputProps('responsive.windowOffsetY')}
              value={resp?.windowOffsetY ?? ''}
            />
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder p="sm" radius="md">
        <Stack gap="sm">
          <Text size="sm" fw={500}>
            響應式開關按鈕相關設定
          </Text>
          <Text size="xs" c="dimmed">
            左右或頂底邊距只要行動版有填寫任何一邊，即會採用行動版的對齊設定；若全未填寫則繼承桌面版設定。
          </Text>
          <Divider style={{ opacity: 0.6 }} />
          <Group grow align="flex-start">
            <NumberInput
              label="響應式開關按鈕寬度 (px)"
              placeholder="未設定則與桌面版相同"
              min={20}
              max={300}
              {...form.getInputProps('responsive.buttonWidth')}
              value={resp?.buttonWidth ?? ''}
            />
            <NumberInput
              label="響應式開關按鈕高度 (px)"
              placeholder="未設定則與桌面版相同"
              min={20}
              max={300}
              {...form.getInputProps('responsive.buttonHeight')}
              value={resp?.buttonHeight ?? ''}
            />
          </Group>
          <Group grow align="flex-start">
            <NumberInput
              label="響應式左側邊距 (px)"
              placeholder="未設定 (若與右側皆填寫則置中)"
              {...form.getInputProps('responsive.positionLeft')}
              value={resp?.positionLeft ?? ''}
            />
            <NumberInput
              label="響應式右側邊距 (px)"
              placeholder="未設定 (若與左側皆填寫則置中)"
              {...form.getInputProps('responsive.positionRight')}
              value={resp?.positionRight ?? ''}
            />
          </Group>
          <Group grow align="flex-start">
            <NumberInput
              label="響應式頂部邊距 (px)"
              placeholder="未設定 (若與底部皆填寫則置中)"
              {...form.getInputProps('responsive.positionTop')}
              value={resp?.positionTop ?? ''}
            />
            <NumberInput
              label="響應式底部邊距 (px)"
              placeholder="未設定 (若與頂部皆填寫則置中)"
              {...form.getInputProps('responsive.positionBottom')}
              value={resp?.positionBottom ?? ''}
            />
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
